import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import { Box, Button, FormControlLabel, Grid, IconButton, MenuItem, Switch, TextField, Typography } from '@mui/material'
import { FieldDefinition } from './useElementDefinitions'

const FIELD_TYPES = ['text', 'select', 'toggle', 'media', 'link', 'collection', 'headline', 'infoText']

interface FieldEditorProps {
    field: FieldDefinition
    onChange: (updated: FieldDefinition) => void
    onDelete: () => void
    depth?: number
}

export const FieldEditor = ({ field, onChange, onDelete, depth = 0 }: FieldEditorProps): JSX.Element => {
    const updateField = (partial: Partial<FieldDefinition>) => {
        onChange({ ...field, ...partial })
    }

    const updateOption = (index: number, key: 'value' | 'label', val: string) => {
        const options = [...(field.options ?? [])]
        options[index] = { ...options[index], [key]: val }
        updateField({ options })
    }

    const addOption = () => {
        updateField({ options: [...(field.options ?? []), { value: '', label: '' }] })
    }

    const removeOption = (index: number) => {
        updateField({ options: (field.options ?? []).filter((_, i) => i !== index) })
    }

    const updateNestedField = (index: number, updated: FieldDefinition) => {
        const fields = [...(field.fields ?? [])]
        fields[index] = updated
        updateField({ fields })
    }

    const addNestedField = () => {
        updateField({
            fields: [...(field.fields ?? []), { type: 'text', label: '', valueName: '' }],
        })
    }

    const removeNestedField = (index: number) => {
        updateField({ fields: (field.fields ?? []).filter((_, i) => i !== index) })
    }

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: depth > 0 ? 'divider' : 'grey.300',
                borderRadius: 1,
                p: 2,
                mb: 1,
                bgcolor: depth > 0 ? 'grey.50' : 'background.paper',
            }}
        >
            <Grid container spacing={2} alignItems="center">
                <Grid item xs={3}>
                    <TextField
                        select
                        label="Type"
                        value={field.type}
                        onChange={(e) => updateField({ type: e.target.value })}
                        fullWidth
                        size="small"
                    >
                        {FIELD_TYPES.map((t) => (
                            <MenuItem key={t} value={t}>
                                {t}
                            </MenuItem>
                        ))}
                    </TextField>
                </Grid>
                <Grid item xs={3}>
                    <TextField
                        label="Label"
                        value={field.label ?? ''}
                        onChange={(e) => updateField({ label: e.target.value })}
                        fullWidth
                        size="small"
                    />
                </Grid>
                <Grid item xs={3}>
                    <TextField
                        label="Value Name"
                        value={field.valueName ?? ''}
                        onChange={(e) => updateField({ valueName: e.target.value })}
                        fullWidth
                        size="small"
                    />
                </Grid>
                <Grid item xs={2}>
                    <TextField
                        label="AI Description"
                        value={field.aiDescription ?? ''}
                        onChange={(e) => updateField({ aiDescription: e.target.value })}
                        fullWidth
                        size="small"
                    />
                </Grid>
                <Grid item xs={1}>
                    <IconButton onClick={onDelete} color="error" size="small">
                        <DeleteIcon />
                    </IconButton>
                </Grid>
            </Grid>

            {/* Type-specific fields */}
            {field.type === 'text' && (
                <Grid container spacing={2} mt={0.5}>
                    <Grid item xs={3}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={field.multiLine ?? false}
                                    onChange={(e) => updateField({ multiLine: e.target.checked })}
                                    size="small"
                                />
                            }
                            label="Multi-line"
                        />
                    </Grid>
                    {field.multiLine && (
                        <Grid item xs={3}>
                            <TextField
                                label="Lines"
                                type="number"
                                value={field.lines ?? 1}
                                onChange={(e) => updateField({ lines: parseInt(e.target.value) || 1 })}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                    )}
                </Grid>
            )}

            {field.type === 'select' && (
                <Box mt={1}>
                    <Grid container spacing={2} mb={1}>
                        <Grid item xs={3}>
                            <TextField
                                select
                                label="Control"
                                value={field.control ?? 'dropdown'}
                                onChange={(e) => updateField({ control: e.target.value })}
                                fullWidth
                                size="small"
                            >
                                <MenuItem value="dropdown">Dropdown</MenuItem>
                                <MenuItem value="radio">Radio</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>
                    <Typography variant="caption" color="text.secondary">
                        Options
                    </Typography>
                    {(field.options ?? []).map((opt, i) => (
                        <Grid container spacing={1} key={i} mb={0.5} alignItems="center">
                            <Grid item xs={4}>
                                <TextField
                                    label="Value"
                                    value={opt.value}
                                    onChange={(e) => updateOption(i, 'value', e.target.value)}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField
                                    label="Label"
                                    value={opt.label}
                                    onChange={(e) => updateOption(i, 'label', e.target.value)}
                                    fullWidth
                                    size="small"
                                />
                            </Grid>
                            <Grid item xs={1}>
                                <IconButton onClick={() => removeOption(i)} size="small" color="error">
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Grid>
                        </Grid>
                    ))}
                    <Button size="small" startIcon={<AddIcon />} onClick={addOption}>
                        Add Option
                    </Button>
                </Box>
            )}

            {field.type === 'link' && (
                <Grid container spacing={2} mt={0.5}>
                    <Grid item>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={field.disableInternal ?? false}
                                    onChange={(e) => updateField({ disableInternal: e.target.checked })}
                                    size="small"
                                />
                            }
                            label="Disable Internal"
                        />
                    </Grid>
                    <Grid item>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={field.disableExternal ?? false}
                                    onChange={(e) => updateField({ disableExternal: e.target.checked })}
                                    size="small"
                                />
                            }
                            label="Disable External"
                        />
                    </Grid>
                    <Grid item>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={field.disableFile ?? false}
                                    onChange={(e) => updateField({ disableFile: e.target.checked })}
                                    size="small"
                                />
                            }
                            label="Disable File"
                        />
                    </Grid>
                </Grid>
            )}

            {field.type === 'headline' && (
                <Grid container spacing={2} mt={0.5}>
                    <Grid item xs={6}>
                        <TextField
                            label="Text"
                            value={field.text ?? ''}
                            onChange={(e) => updateField({ text: e.target.value })}
                            fullWidth
                            size="small"
                        />
                    </Grid>
                </Grid>
            )}

            {field.type === 'collection' && (
                <Box mt={1}>
                    <Grid container spacing={2} mb={1}>
                        <Grid item xs={3}>
                            <TextField
                                label="Title"
                                value={field.title ?? ''}
                                onChange={(e) => updateField({ title: e.target.value })}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                label="Item Title"
                                value={field.itemTitle ?? ''}
                                onChange={(e) => updateField({ itemTitle: e.target.value })}
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={1.5}>
                            <TextField
                                label="Min"
                                type="number"
                                value={field.min ?? ''}
                                onChange={(e) =>
                                    updateField({ min: e.target.value ? parseInt(e.target.value) : undefined })
                                }
                                fullWidth
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={1.5}>
                            <TextField
                                label="Max"
                                type="number"
                                value={field.max ?? ''}
                                onChange={(e) =>
                                    updateField({ max: e.target.value ? parseInt(e.target.value) : undefined })
                                }
                                fullWidth
                                size="small"
                            />
                        </Grid>
                    </Grid>
                    <Typography variant="caption" color="text.secondary">
                        Nested Fields
                    </Typography>
                    <Box ml={2}>
                        {(field.fields ?? []).map((nested, i) => (
                            <FieldEditor
                                key={i}
                                field={nested}
                                onChange={(updated) => updateNestedField(i, updated)}
                                onDelete={() => removeNestedField(i)}
                                depth={depth + 1}
                            />
                        ))}
                        <Button size="small" startIcon={<AddIcon />} onClick={addNestedField}>
                            Add Nested Field
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    )
}
